import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { cx } from "./cx";
import styles from "./Modal.module.css";

export type ModalProps = {
  open: boolean;
  /** Appelé à `Escape`, au clic sur le fond et au bouton de fermeture. */
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Boutons du bas : confirmer, annuler. */
  footer?: ReactNode;
  className?: string;
};

/**
 * Boîte de dialogue, sur `<dialog>` natif.
 *
 * `showModal()` donne gratuitement ce qu'une implémentation maison oublie
 * toujours : le piège de focus, `Escape`, la couche supérieure au-dessus des
 * toasts, et le reste de la page rendu inerte. Le `<dialog>` hérite des
 * variables CSS de son parent DOM : l'accent d'une entreprise s'y applique.
 */
export function Modal({ open, onClose, title, children, footer, className }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const idTitre = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) {
      return;
    }
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={cx(styles.dialogue, className)}
      aria-labelledby={idTitre}
      // `close` couvre Escape et `dialog.close()` : on remonte l'état au parent,
      // qui reste seul maître de `open`.
      onClose={onClose}
      onClick={(e) => {
        // Un clic sur le fond (l'élément dialog lui-même, hors de sa boîte).
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.boite}>
        <header className={styles.entete}>
          <h2 id={idTitre} className={styles.titre}>
            {title}
          </h2>
          <button type="button" className={styles.fermer} onClick={onClose} aria-label="Fermer">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div className={styles.corps}>{children}</div>
        {footer !== undefined && <footer className={styles.pied}>{footer}</footer>}
      </div>
    </dialog>
  );
}
